import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import { firebase_firestore, realtime_db } from '../../../config/firebaseConfig'
import { useCollectionDataOnce } from 'react-firebase-hooks/firestore'
import { collection, query, where, deleteDoc, doc, updateDoc } from 'firebase/firestore'
import { ref, remove } from 'firebase/database'

import { useObject, useObjectVal } from 'react-firebase-hooks/database'
import {
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EditIcon from '@mui/icons-material/Edit'
import { useContext, useState, useEffect } from 'react'
import { SnackbarHelperContext } from '../../../common/contexts/SnackbarHelperContext'
import { useCallableRef } from '../../../api/useCallableRef'
import UserFeatureTags from './UserFeatureTags'
import {
  sectionCard,
  sectionHeader,
  sectionTitle,
  sectionBody,
  tableHeadCell,
  tableBodyCell,
  adminColors,
} from '../../adminStyles'

export default function TraderMoreInfo(props: any) {
  const { trader, onTraderUpdate } = props

  const [tradingPlans, , , tradingPlansSnapshot, reloadTradingPlans] = useCollectionDataOnce(
    query(collection(firebase_firestore, `tradingPlans`), where('user.gcid', '==', trader.gcid))
  )
  const [gtfLastLogin] = useObjectVal(ref(realtime_db, `usermeta/${trader.gcid}/gtf/lastLogin`))
  const [greenchartLastLogin] = useObjectVal(
    ref(realtime_db, `usermeta/${trader.gcid}/greenchart/lastLogin`)
  )
  const [patternAlertAcceptedSnapshot] = useObject(
    ref(realtime_db, `usermeta/${trader.gcid}/patternAlertAccepted`)
  )
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [, setAuthToken] = useState<string>()

  const [createToken, loadingCreateToken] = useCallableRef('createTokenCallable')

  const [showDevTools, setShowDevTools] = useState(false)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.metaKey && e.key === 'j') {
        e.preventDefault()
        setShowDevTools((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const firebaseConsoleUrl = `https://console.firebase.google.com/project/greenchart-93a2a/firestore/databases/-default-/data/~2Fusermeta~2F${trader.gcid}`

  const [loadingTradingToggles, setLoadingTradingToggles] = useState<{ [key: string]: boolean }>({})
  const [expandedHistory, setExpandedHistory] = useState<Record<string, boolean>>({})
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false)
  const [creditsToAdd, setCreditsToAdd] = useState<string>('')
  const [addingCredits, setAddingCredits] = useState(false)
  const [resettingNotifications, setResettingNotifications] = useState(false)
  const [editMaxAutoTradersOpen, setEditMaxAutoTradersOpen] = useState(false)
  const [maxAutoTradersValue, setMaxAutoTradersValue] = useState<string>('')
  const [savingMaxAutoTraders, setSavingMaxAutoTraders] = useState(false)

  async function toggleAlertTrading(tradingPlan: any, alertRef: any) {
    const planId = tradingPlan.id
    setLoadingTradingToggles((prev) => ({ ...prev, [planId]: true }))

    try {
      if (!alertRef) {
        throw new Error('Alert reference not found')
      }

      const isEnabled = tradingPlan.trading?.enabled

      if (isEnabled) {
        await updateDoc(alertRef, { 'trading.enabled': false })
      } else {
        await updateDoc(alertRef, { 'trading.enabled': true })
      }

      setSnackbarSettings({
        severity: 'success',
        message: `Trading ${isEnabled ? 'disabled' : 'enabled'} for alert!`,
        autoHideDuration: 6000,
      })

      reloadTradingPlans()
    } catch (err: any) {
      console.log(err)
      setSnackbarSettings({
        severity: 'error',
        message: `Error toggling trading for alert`,
        autoHideDuration: 6000,
      })
    } finally {
      setLoadingTradingToggles((prev) => ({ ...prev, [planId]: false }))
    }
  }

  async function removeAlert(alertRef: any) {
    try {
      await deleteDoc(alertRef)
      setSnackbarSettings({
        severity: 'success',
        message: 'Alert deleted!',
        autoHideDuration: 6000,
      })
      reloadTradingPlans()
    } catch (err: any) {
      console.log(err)
      setSnackbarSettings({
        severity: 'error',
        message: `Error deleting alert`,
        autoHideDuration: 6000,
      })
    }
  }

  const STATUS_CHIP_COLORS: Record<string, { bg: string; text: string }> = {
    member: { bg: 'rgba(27, 138, 42, 0.1)', text: adminColors.profitGreen },
    free: { bg: 'rgba(24, 187, 14, 0.1)', text: adminColors.accent },
    trial: { bg: 'rgba(184, 134, 11, 0.1)', text: adminColors.warningAmber },
    canceled: { bg: 'rgba(201, 55, 44, 0.08)', text: adminColors.lossRed },
    hold: { bg: 'rgba(140, 147, 155, 0.1)', text: adminColors.textMuted },
    starter: { bg: 'rgba(100, 100, 200, 0.1)', text: '#5555bb' },
  }

  function getStatusChipStyle(status: string) {
    const colors = STATUS_CHIP_COLORS[status] || {
      bg: 'rgba(140, 147, 155, 0.08)',
      text: adminColors.textMuted,
    }
    return { bgcolor: colors.bg, color: colors.text, borderColor: 'transparent' }
  }

  function renderTradingHouseHistory(label: string, tradingHouse: any) {
    if (!tradingHouse) return null

    const currentStatus = tradingHouse.currentStatus?.status
    const history = tradingHouse.statusHistory || []
    // Show most recent first
    const sortedHistory = [...history].reverse()

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <Typography
            sx={{
              fontSize: '0.775rem',
              fontWeight: 600,
              color: adminColors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {label}
          </Typography>
          {currentStatus && (
            <Chip
              label={currentStatus}
              size="small"
              sx={{
                ...getStatusChipStyle(currentStatus),
                fontSize: '0.7rem',
                fontWeight: 600,
                height: 20,
                textTransform: 'capitalize',
              }}
            />
          )}
        </Box>
        {sortedHistory.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {(expandedHistory[label] ? sortedHistory : sortedHistory.slice(0, 3)).map(
              (entry: any, i: number) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 0.25,
                    px: 0.5,
                    borderRadius: '4px',
                    bgcolor: i === 0 ? 'rgba(24, 187, 14, 0.04)' : 'transparent',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.775rem',
                      fontWeight: i === 0 ? 500 : 400,
                      color: i === 0 ? adminColors.textPrimary : adminColors.textSecondary,
                      textTransform: 'capitalize',
                    }}
                  >
                    {entry.status}
                  </Typography>
                  <Typography sx={{ fontSize: '0.725rem', color: adminColors.textMuted }}>
                    {entry.timestamp?.seconds
                      ? new Date(entry.timestamp.seconds * 1000).toLocaleDateString()
                      : '—'}
                  </Typography>
                </Box>
              )
            )}
            {sortedHistory.length > 3 && (
              <Typography
                onClick={() => setExpandedHistory((prev) => ({ ...prev, [label]: !prev[label] }))}
                sx={{
                  fontSize: '0.725rem',
                  color: adminColors.accent,
                  cursor: 'pointer',
                  px: 0.5,
                  pt: 0.25,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {expandedHistory[label] ? 'Show less' : `Show ${sortedHistory.length - 3} more`}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.775rem', color: adminColors.textMuted }}>
            No history
          </Typography>
        )}
      </Box>
    )
  }

  function renderTradingHouses() {
    const gc = trader.tradingHouses?.greenchart
    const gtf = trader.tradingHouses?.gtf

    if (!gc && !gtf) return null

    return (
      <Box sx={sectionCard}>
        <Box sx={sectionHeader}>
          <Typography sx={sectionTitle}>Trading Houses</Typography>
        </Box>
        <Box sx={{ ...sectionBody, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {renderTradingHouseHistory('GreenChart', gc)}
          {renderTradingHouseHistory('GTF', gtf)}
        </Box>
      </Box>
    )
  }

  function renderTradingPlans() {
    const visiblePlans = tradingPlans?.filter(
      (tp: any) => !tp.alertTypes?.includes('exitTradingPlan')
    )
    if (!visiblePlans || !visiblePlans.length) {
      return
    }

    const tpaAffiliateAlertId = trader.tradingHouses?.gtf?.tpaAffiliateAlertId

    return (
      <Box sx={sectionCard}>
        <Box sx={sectionHeader}>
          <Typography sx={sectionTitle}>Trading Plans</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={tableHeadCell}>Name</TableCell>
              <TableCell sx={tableHeadCell}>Active</TableCell>
              <TableCell sx={tableHeadCell}>Delivery</TableCell>
              <TableCell sx={tableHeadCell}>Trading</TableCell>
              <TableCell sx={tableHeadCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visiblePlans.map((tradingPlan: any) => {
              const docRef = tradingPlansSnapshot?.docs.find((d) => d.id === tradingPlan.id)?.ref
              return (
                <TableRow key={tradingPlan.id} hover>
                  <TableCell sx={tableBodyCell}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {tradingPlan.name}
                      {tpaAffiliateAlertId && tradingPlan.id === tpaAffiliateAlertId && (
                        <Chip
                          label="TPA Affiliate"
                          size="small"
                          sx={{
                            fontSize: '0.65rem',
                            height: 18,
                            bgcolor: 'rgba(24, 187, 14, 0.1)',
                            color: adminColors.accent,
                            borderColor: 'transparent',
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={tableBodyCell}>{tradingPlan.active ? 'Yes' : 'No'}</TableCell>
                  <TableCell sx={tableBodyCell}>
                    {[...new Set(tradingPlan.alertTypes)].join(', ')}
                  </TableCell>
                  <TableCell sx={tableBodyCell}>
                    <Tooltip
                      title={
                        tradingPlan.trading?.enabled
                          ? 'Disable trading for this alert'
                          : 'Enable trading for this alert'
                      }
                    >
                      <IconButton
                        onClick={() => toggleAlertTrading(tradingPlan, docRef)}
                        disabled={loadingTradingToggles[tradingPlan.id]}
                        size="small"
                        sx={{
                          color: tradingPlan.trading?.enabled
                            ? adminColors.accent
                            : adminColors.lossRed,
                        }}
                      >
                        <AttachMoneyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={tableBodyCell}>
                    <IconButton
                      size="small"
                      onClick={() => removeAlert(docRef)}
                      sx={{ color: adminColors.textMuted }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Box>
    )
  }

  function renderInfoRow(label: string, value: string | number) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography sx={{ fontSize: '0.8rem', color: adminColors.textSecondary }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: adminColors.textPrimary }}>
          {value}
        </Typography>
      </Box>
    )
  }

  function renderProducts() {
    const hasAlerts = trader.products?.alerts
    const hasBacktest = trader.products?.backtest
    const productName = trader.products?.alerts?.name

    if (!hasAlerts && !hasBacktest) {
      return null
    }

    return (
      <Box sx={sectionCard}>
        <Box sx={sectionHeader}>
          <Typography sx={sectionTitle}>Products</Typography>
          {productName && (
            <Typography sx={{ fontSize: '0.775rem', color: adminColors.textSecondary }}>
              {productName}
            </Typography>
          )}
        </Box>
        <Box sx={{ ...sectionBody, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Alert Package */}
          {hasAlerts && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.775rem',
                  fontWeight: 600,
                  color: adminColors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mb: 0.75,
                }}
              >
                Alerts
              </Typography>
              {renderInfoRow(
                'Alerts',
                `${tradingPlans?.filter((tp: any) => !tp.alertTypes?.includes('exitTradingPlan')).length || 0} / ${trader.products.alerts.alertCount}`
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: adminColors.textSecondary }}>
                  Notifications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Reset notifications to cap">
                    <IconButton
                      size="small"
                      onClick={handleResetNotifications}
                      disabled={resettingNotifications}
                      sx={{ color: adminColors.accent }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    sx={{ fontSize: '0.8rem', fontWeight: 500, color: adminColors.textPrimary }}
                  >
                    {trader.products.alerts.notificationsRemaining} /{' '}
                    {trader.products.alerts.notifications}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 0.5,
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontSize: '0.8rem', color: adminColors.textSecondary }}>
                  Max AutoTraders
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title="Edit max autotraders">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMaxAutoTradersValue(
                          String(trader.products.alerts.maxAutoTradeAlertsAllowed ?? 0)
                        )
                        setEditMaxAutoTradersOpen(true)
                      }}
                      sx={{ color: adminColors.accent }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Typography
                    sx={{ fontSize: '0.8rem', fontWeight: 500, color: adminColors.textPrimary }}
                  >
                    {trader.products.alerts.maxAutoTradeAlertsAllowed ?? 0}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Backtest Package */}
          {hasBacktest && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.775rem',
                  fontWeight: 600,
                  color: adminColors.textSecondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mb: 0.75,
                }}
              >
                Backtest
              </Typography>
              {renderInfoRow(
                'Credits Used',
                `${(trader.products.backtest.accruedCost || 0).toFixed(2)} / ${(trader.products.backtest.maxCost ?? 0).toFixed(2)}`
              )}
              {renderInfoRow('Recurring Credits', trader.products.backtest.recurringCost ?? 0)}
              {(trader.products?.backtest?.maxCost ?? 0) >
                (trader.products?.backtest?.recurringCost ?? 0) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '0.8rem', color: adminColors.textSecondary }}>
                    Purchased Credits
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Add purchased credits">
                      <IconButton
                        size="small"
                        onClick={() => setAddCreditsDialogOpen(true)}
                        sx={{ color: adminColors.accent }}
                      >
                        <AddIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Typography
                      sx={{ fontSize: '0.8rem', fontWeight: 500, color: adminColors.textPrimary }}
                    >
                      {(
                        (trader.products.backtest.maxCost ?? 0) -
                        (trader.products.backtest.recurringCost ?? 0)
                      ).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Feature Tags inline */}
          <Box sx={{ borderTop: `1px solid ${adminColors.borderLight}`, pt: 1.5 }}>
            <UserFeatureTags trader={trader} onTraderUpdate={onTraderUpdate} />
          </Box>
        </Box>
      </Box>
    )
  }

  function renderSubscriptionDetails() {
    if (!trader.subscription) {
      return
    }

    return (
      <Box sx={sectionCard}>
        <Box sx={sectionHeader}>
          <Typography sx={sectionTitle}>Subscription</Typography>
        </Box>
        <Box sx={sectionBody}>
          {trader.subscription.trialPeriod &&
            renderInfoRow(
              'Alerts Trial Ends',
              new Date(trader.subscription.trialPeriod * 1000).toLocaleDateString()
            )}
          {renderInfoRow(
            'Renewal Date',
            new Date(trader.subscription.renewalDate * 1000).toLocaleDateString()
          )}
          {renderInfoRow('Packages', trader.subscription.packages.join(', '))}
        </Box>
      </Box>
    )
  }

  function renderLastLogin(label: string, datetime: any) {
    if (!datetime) {
      return
    }
    return (
      <Typography sx={{ fontSize: '0.75rem', color: adminColors.textMuted }}>
        {label}: {new Date(datetime).toLocaleString()}
      </Typography>
    )
  }

  async function removePatternAlertAccepted() {
    try {
      if (patternAlertAcceptedSnapshot?.ref) {
        await remove(patternAlertAcceptedSnapshot?.ref)
        setSnackbarSettings({
          severity: 'success',
          message: 'Pattern alert accepted deleted!',
          autoHideDuration: 6000,
        })
      }
    } catch (err: any) {
      setSnackbarSettings({ severity: 'error', message: `Error deleting.`, autoHideDuration: 6000 })
    }
  }

  async function handleSaveMaxAutoTraders() {
    const newValue = parseInt(maxAutoTradersValue)
    if (isNaN(newValue) || newValue < 0) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Please enter a valid number',
        autoHideDuration: 6000,
      })
      return
    }
    setSavingMaxAutoTraders(true)
    try {
      const usermetaRef = doc(firebase_firestore, 'usermeta', trader.gcid)
      await updateDoc(usermetaRef, { 'products.alerts.maxAutoTradeAlertsAllowed': newValue })
      setEditMaxAutoTradersOpen(false)
      setMaxAutoTradersValue('')
      setSnackbarSettings({
        severity: 'success',
        message: `Max AutoTraders set to ${newValue}`,
        autoHideDuration: 6000,
      })
      if (onTraderUpdate) {
        onTraderUpdate({
          ...trader,
          products: {
            ...trader.products,
            alerts: { ...trader.products.alerts, maxAutoTradeAlertsAllowed: newValue },
          },
        })
      }
    } catch (err: any) {
      setSnackbarSettings({
        severity: 'error',
        message: `Error updating Max AutoTraders`,
        autoHideDuration: 6000,
      })
    } finally {
      setSavingMaxAutoTraders(false)
    }
  }

  async function handleAddBacktestCredits() {
    const credits = parseInt(creditsToAdd)
    if (isNaN(credits) || credits <= 0) {
      setSnackbarSettings({
        severity: 'error',
        message: 'Please enter a valid number of credits',
        autoHideDuration: 6000,
      })
      return
    }

    setAddingCredits(true)
    try {
      const usermetaRef = doc(firebase_firestore, 'usermeta', trader.gcid)
      const currentBacktest = trader.products?.backtest

      if (!currentBacktest) {
        throw new Error('User does not have backtest product')
      }

      const recurringCost = currentBacktest.recurringCost ?? 0
      const currentMaxCost = currentBacktest.maxCost ?? recurringCost
      const newMaxCost = currentMaxCost + credits

      await updateDoc(usermetaRef, {
        'products.backtest.maxCost': newMaxCost,
      })

      // Create updated trader object with new maxCost
      const updatedTrader = {
        ...trader,
        products: {
          ...trader.products,
          backtest: {
            ...trader.products.backtest,
            maxCost: newMaxCost,
          },
        },
      }

      // Close dialog and clear input
      setAddCreditsDialogOpen(false)
      setCreditsToAdd('')

      setSnackbarSettings({
        severity: 'success',
        message: `Added ${credits} backtest credits! New max: ${newMaxCost}`,
        autoHideDuration: 6000,
      })

      // Update parent with the new trader object
      if (onTraderUpdate) {
        onTraderUpdate(updatedTrader)
      }
    } catch (err: any) {
      console.error('Error adding credits:', err)
      setSnackbarSettings({
        severity: 'error',
        message: `Error adding credits: ${err.message || 'Unknown error'}`,
        autoHideDuration: 6000,
      })
    } finally {
      setAddingCredits(false)
    }
  }

  async function handleResetNotifications() {
    setResettingNotifications(true)
    try {
      const usermetaRef = doc(firebase_firestore, 'usermeta', trader.gcid)
      const currentAlerts = trader.products?.alerts

      if (!currentAlerts) {
        throw new Error('User does not have alerts product')
      }

      const notificationsCap = currentAlerts.notifications

      await updateDoc(usermetaRef, {
        'products.alerts.notificationsRemaining': notificationsCap,
      })

      // Create updated trader object
      const updatedTrader = {
        ...trader,
        products: {
          ...trader.products,
          alerts: {
            ...trader.products.alerts,
            notificationsRemaining: notificationsCap,
          },
        },
      }

      setSnackbarSettings({
        severity: 'success',
        message: `Notifications reset to ${notificationsCap}!`,
        autoHideDuration: 6000,
      })

      // Update parent with the new trader object
      if (onTraderUpdate) {
        onTraderUpdate(updatedTrader)
      }
    } catch (err: any) {
      console.error('Error resetting notifications:', err)
      setSnackbarSettings({
        severity: 'error',
        message: `Error resetting notifications: ${err.message || 'Unknown error'}`,
        autoHideDuration: 6000,
      })
    } finally {
      setResettingNotifications(false)
    }
  }

  async function copyMemberCenterLink() {
    const token = await createToken({ gcid: trader.gcid })
    if (token && token.data) {
      setAuthToken(`${token.data}`)
      const url = `myaccount.greenchart.com?authToken=${token.data}`
      navigator.clipboard.writeText(url)
      setSnackbarSettings({
        severity: 'info',
        message: 'Copied to clipboard!',
        autoHideDuration: 6000,
      })
    }
  }

  async function copyLocalLink() {
    const token = await createToken({ gcid: trader.gcid })
    if (token && token.data) {
      navigator.clipboard.writeText(`http://localhost:3000?authToken=${token.data}`)
      setSnackbarSettings({
        severity: 'info',
        message: 'Localhost link copied!',
        autoHideDuration: 6000,
      })
    }
  }

  const linkButtonSx = {
    textTransform: 'none' as const,
    fontSize: '0.775rem',
    fontWeight: 500,
    color: adminColors.accent,
    justifyContent: 'flex-start',
    px: 0,
    py: 0.25,
    minWidth: 0,
    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
  }

  return (
    <>
      <Box
        sx={{
          ...sectionCard,
          overflow: 'auto',
        }}
      >
        {/* User header */}
        <Box
          sx={{
            ...sectionHeader,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: '0.95rem', fontWeight: 600, color: adminColors.textPrimary }}
            >
              {trader.email}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: adminColors.textMuted,
                  fontFamily: 'monospace',
                }}
              >
                {trader.gcid}
              </Typography>
              <Tooltip title="Open in Firebase Console">
                <IconButton
                  size="small"
                  onClick={() => window.open(firebaseConsoleUrl, '_blank')}
                  sx={{ color: adminColors.textMuted, p: 0.25 }}
                >
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
              {renderLastLogin('DTU', gtfLastLogin)}
              {renderLastLogin('GreenChart', greenchartLastLogin)}
            </Box>
          </Box>
        </Box>

        <Box sx={{ ...sectionBody, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Quick links */}
          <Box sx={sectionCard}>
            <Box sx={sectionHeader}>
              <Typography sx={sectionTitle}>Quick Links</Typography>
            </Box>
            <Box sx={{ ...sectionBody, py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.775rem', color: adminColors.textSecondary }}>
                  Alert accepted:
                  {patternAlertAcceptedSnapshot?.exists()
                    ? ` ${new Date(patternAlertAcceptedSnapshot.val()).toLocaleString()}`
                    : ' Never'}
                </Typography>
                {patternAlertAcceptedSnapshot?.exists() && (
                  <IconButton size="small" onClick={removePatternAlertAccepted}>
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
              <Stack direction="column" alignItems="flex-start" sx={{ mt: 0.5 }}>
                <Button
                  sx={linkButtonSx}
                  disabled={loadingCreateToken}
                  onClick={copyMemberCenterLink}
                >
                  {loadingCreateToken ? 'creating...' : 'Copy Member Center Link'}
                </Button>
                {showDevTools && (
                  <Button sx={linkButtonSx} disabled={loadingCreateToken} onClick={copyLocalLink}>
                    {loadingCreateToken ? 'creating...' : 'Copy Localhost Link'}
                  </Button>
                )}
              </Stack>
            </Box>
          </Box>

          {renderTradingHouses()}
          {renderProducts()}
          {renderSubscriptionDetails()}
          {renderTradingPlans()}
        </Box>
      </Box>

      {/* Edit Max AutoTraders Dialog */}
      <Dialog
        open={editMaxAutoTradersOpen}
        onClose={() => !savingMaxAutoTraders && setEditMaxAutoTradersOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: adminColors.textPrimary }}>Set Max AutoTraders</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: adminColors.textSecondary, mb: 2 }}>
            Current value: {trader.products?.alerts?.maxAutoTradeAlertsAllowed ?? 0}
          </Typography>
          <TextField
            autoFocus
            label="Max AutoTraders"
            type="number"
            fullWidth
            value={maxAutoTradersValue}
            onChange={(e) => setMaxAutoTradersValue(e.target.value)}
            disabled={savingMaxAutoTraders}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditMaxAutoTradersOpen(false)}
            disabled={savingMaxAutoTraders}
            sx={{ color: adminColors.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveMaxAutoTraders}
            disabled={savingMaxAutoTraders}
            sx={{ color: adminColors.accent }}
          >
            {savingMaxAutoTraders ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Backtest Credits Dialog */}
      <Dialog
        open={addCreditsDialogOpen}
        onClose={() => !addingCredits && setAddCreditsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: adminColors.textPrimary }}>Add Backtest Credits</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: adminColors.textSecondary, mb: 2 }}>
            Current max credits: {trader.products?.backtest?.maxCost ?? 0}
          </Typography>
          <TextField
            autoFocus
            label="Credits to Add"
            type="number"
            fullWidth
            value={creditsToAdd}
            onChange={(e) => setCreditsToAdd(e.target.value)}
            disabled={addingCredits}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddCreditsDialogOpen(false)}
            disabled={addingCredits}
            sx={{ color: adminColors.textSecondary }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddBacktestCredits}
            disabled={addingCredits}
            sx={{ color: adminColors.accent }}
          >
            {addingCredits ? 'Adding...' : 'Add Credits'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
